/**
 * User settings and profile screens
 */

import { defRoute, useRouteParams } from '@components/router';

// Tabs nav
import { Tab, Tabs } from '@components/tabs';

// Settings screens
import { ProfileTab } from './profile-tab';
import { PasswordTab } from '@components/password-form';
import { StripeOauthRedirect } from './stripe-integration';
import { useAuth } from '@components/router/session-context';

// Layout and forms
import { hasLevel } from 'shared/auth';
import { LoadingIndicator } from '@components/loading-indicator';
import { ZoomOauthRedirect } from './zoom-integration';
import { BillingTab } from './billing-tab';
import { SiteTab } from './site-tab';
import { useIntl } from 'shared/intl/use-intl';
import { IntegrationsTab } from './integrations-tab';
import { useEffect } from 'preact/hooks';
import { GiftsTab } from './gifts-tab';
import { DeleteAccountTab } from './delete-account-tab';
import { RootPageLayout } from '@components/root-page-layout';

export const accountPage = {
  route: defRoute({
    Page,
    authLevel: 'student',
  }),
};

export const v1AcctRedirect = {
  route: defRoute({
    Page({ router }) {
      useEffect(() => {
        router.goto('/account');
      }, []);
      return null;
    },
  }),
};

/**
 * This is the URL that Stripe returns to when performing a connect operation.
 * We'll render the integrations tab in this scenario, and it will take care of
 * handling any remaining connect logic.
 */
export const integrationsPage = {
  route: defRoute({
    Page,
    authLevel: 'guide',
  }),
};

export const stripeOauthRedirect = {
  route: defRoute({
    Page: StripeOauthRedirect,
    isPublic: true,
  }),
};

export const zoomOauthRedirect = {
  route: defRoute({
    Page: ZoomOauthRedirect,
    isPublic: true,
  }),
};

function Page() {
  const { tab = 'profile' } = useRouteParams();
  const { user } = useAuth();
  const isGuide = hasLevel(user, 'guide');
  const isAdmin = hasLevel(user, 'admin');

  return (
    <RootPageLayout activeTab="account" title="Account" hideTitle>
      <Content tab={tab} isGuide={isGuide} isAdmin={isAdmin} />
    </RootPageLayout>
  );
}

function Content({ tab, isGuide, isAdmin }: { tab: string; isGuide: boolean; isAdmin: boolean }) {
  const intl = useIntl();
  const { user, setUser } = useAuth();

  if (!user) {
    return <LoadingIndicator />;
  }

  return (
    <div class="py-6">
      <div class="minidoc-scroll-container">
        <Tabs>
          <Tab isSelected={tab === 'profile'} href="/account/profile">
            {intl('Profile')}
          </Tab>
          <Tab isSelected={tab === 'password'} href="/account/password">
            {intl('Password')}
          </Tab>
          {isGuide && (
            <Tab isSelected={tab === 'integrations'} href="/account/integrations">
              Integrations
            </Tab>
          )}
          <Tab isSelected={tab === 'billing'} href="/account/billing">
            Billing
          </Tab>
          {user.tier === 'pro' && (
            <Tab isSelected={tab === 'site'} href="/account/site">
              Site
            </Tab>
          )}
          <Tab isSelected={tab === 'gifts'} href="/account/gifts">
            {intl('Gifts')}
          </Tab>
          {!isAdmin && (
            <Tab isSelected={tab === 'delete'} href="/account/delete">
              {intl('Delete Account')}
            </Tab>
          )}
        </Tabs>
        <div class="flex flex-col pb-20">
          {(tab === 'init' || tab === 'profile') && <ProfileTab user={user} setUser={setUser} />}
          {tab === 'integrations' && <IntegrationsTab />}
          {tab === 'password' && <PasswordTab />}
          {tab === 'billing' && <BillingTab />}
          {tab === 'site' && <SiteTab />}
          {tab === 'gifts' && <GiftsTab />}
          {tab === 'delete' && !isAdmin && <DeleteAccountTab />}
        </div>
      </div>
    </div>
  );
}
